import dayjs from 'dayjs';
import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { BsArrowCounterclockwise, BsCalendar2, BsGeoAlt } from 'react-icons/bs';

import { PackageType, type Booking } from '@/models';
import {
  BookingStatusBadge,
  CashWalletBadge,
  MinutesBadge,
  PercentBadge,
  TimesBadge,
} from '@/pages/components/Badge';

const formatBookingDateTime = (isoDateTime: string): string => {
  return dayjs(isoDateTime).format('DD/MM/YYYY (HH:mm)');
};

export interface BookingHistoryItemProps {
  booking: Booking;
  onReorderClick: () => void;
  onClick: () => void;
  packageUsageAmount?: number;
  packageType?: PackageType;
}

const BookingHistoryItem: FC<BookingHistoryItemProps> = ({
  booking,
  onReorderClick,
  onClick,
  packageUsageAmount,
  packageType,
}) => {
  const { t } = useTranslation();
  const bookingDateTime = useMemo(
    () => dayjs(booking.availabilityId),
    [booking.availabilityId],
  );
  const storeDisplayName = useMemo(() => {
    return [booking.storeName, booking.storeBranchName]
      .filter(Boolean)
      .join(' - ');
  }, [booking.storeBranchName, booking.storeName]);

  const services = useMemo(() => {
    const labels: string[] = [];
    booking.customers.forEach((customer) => {
      customer.treatmentItems.forEach((item) => {
        const label =
          item.treatmentName ??
          t('components.bookingDetails.treatmentFallback', {
            id: item.treatmentId,
          });
        labels.push(label);
      });
    });

    const unique: string[] = [];
    const seen = new Set<string>();
    labels.forEach((label) => {
      if (seen.has(label)) return;
      seen.add(label);
      unique.push(label);
    });
    return unique;
  }, [booking.customers, t]);

  const title = services.length > 0 ? services.join(' / ') : '—';
  const guestCount = booking.customers.length;
  const isToday = bookingDateTime.isSame(dayjs(), 'day');
  const isTomorrow = bookingDateTime.isSame(dayjs().add(1, 'day'), 'day');

  const relativeDateCallout = isToday
    ? t('profile.home.bookingDate.todayCallout')
    : isTomorrow
      ? t('profile.home.bookingDate.tomorrowCallout')
      : null;

  const usageBadge = useMemo(() => {
    if (packageUsageAmount === undefined) return null;

    const amountLabel = packageUsageAmount.toLocaleString();
    switch (packageType) {
      case PackageType.Times:
        return (
          <TimesBadge
            text={t('package.detailsPage.usage.badge.timesUsed', {
              amount: amountLabel,
            })}
          />
        );
      case PackageType.Minutes:
        return (
          <MinutesBadge
            text={t('package.detailsPage.usage.badge.minutesUsed', {
              amount: amountLabel,
            })}
          />
        );
      case PackageType.CashWallet:
        return (
          <CashWalletBadge
            text={t('package.detailsPage.usage.badge.cashWalletUsed', {
              amount: amountLabel,
            })}
          />
        );
      case PackageType.Percent:
        return (
          <PercentBadge
            text={t('package.detailsPage.usage.badge.percentDiscount', {
              amount: amountLabel,
            })}
          />
        );
      default:
        return null;
    }
  }, [packageType, packageUsageAmount, t]);

  const showMetaRow = booking.status || usageBadge;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="border-border-inactive cursor-pointer rounded-lg border py-3"
    >
      <div className="flex flex-col gap-1 px-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <div className="bg-profile-tag-bg flex size-6 shrink-0 items-center justify-center rounded-sm">
              <span className="text-profile-tag-text text-sm leading-5 font-semibold">
                {guestCount}
              </span>
            </div>
            <p className="truncate text-base leading-5 text-black">{title}</p>
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onReorderClick();
            }}
            className="bg-primary-50 flex h-7 shrink-0 items-center gap-1.5 rounded px-2"
          >
            <BsArrowCounterclockwise className="text-primary-text size-3" />
            <span className="text-primary-text text-sm leading-4 font-semibold">
              {t('profile.home.actions.reorder')}
            </span>
          </button>
        </div>

        {storeDisplayName ? (
          <div className="flex items-center gap-2">
            <BsGeoAlt className="text-text-muted size-4" />
            <p className="text-text-secondary-light truncate text-sm leading-5">
              {storeDisplayName}
            </p>
          </div>
        ) : null}

        <div className="flex min-w-0 items-center gap-2">
          <BsCalendar2 className="text-text-muted size-4" />
          <div className="flex min-w-0 items-center gap-2">
            <p className="text-text-secondary-light truncate text-sm leading-5">
              {formatBookingDateTime(booking.availabilityId)}
            </p>

            {relativeDateCallout ? (
              <span className="text-error shrink-0 text-sm leading-5">
                {relativeDateCallout}
              </span>
            ) : null}
          </div>
        </div>

        {showMetaRow ? (
          <div className="flex items-center gap-3 pt-1">
            <div className="flex flex-wrap items-center gap-2">
              <BookingStatusBadge status={booking.status} />
              {usageBadge}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BookingHistoryItem;
